import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useBranchLimits, useOutlets, useSaveBranchLimit, useSaveServiceType } from '@/api/hooks'
import type { ServiceTypeView } from '@/api/types'
import { Dialog } from '@/components/AppDialog'
import { ErrorBanner } from '@/components/common'

/** Empty is no limit of its own, which is not the same as a limit of nothing. */
const asField = (value: number | null | undefined) => (value == null ? '' : String(value))
const asStored = (value: string) => (value.trim() === '' ? null : Number(value))

/**
 * How many each branch will take a service for.
 *
 * <p>The business says what a line of business is worth doing at all. A branch
 * says what its own kitchen and its own rooms can actually do - the same
 * outdoor catering is run for two thousand out of the main kitchen and four
 * hundred out of the small one - and when the two disagree the branch is the
 * one taking the booking.</p>
 *
 * <p>Left blank, a branch follows the business. Both boxes emptied withdraws
 * whatever it had said of its own, rather than recording two zeroes.</p>
 */
export function BranchLimitsDialog({
  service,
  onClose,
}: {
  service: ServiceTypeView | null
  onClose: () => void
}) {
  const { data: outlets } = useOutlets()
  const { data: limits } = useBranchLimits(service?.id)
  const save = useSaveBranchLimit()
  const saveService = useSaveServiceType()
  // The default every branch follows unless it says otherwise. It lives here
  // rather than on the services table so the default and the exceptions to it
  // are read and set in one place.
  const [everywhere, setEverywhere] = useState({ minPax: '', maxPax: '' })

  // Held here while they are typed, so a half-entered pair is not saved on
  // every keystroke and the whole table can be put right in one go.
  const [draft, setDraft] = useState<Record<string, { minPax: string; maxPax: string }>>({})

  useEffect(() => {
    if (!service) return
    const next: Record<string, { minPax: string; maxPax: string }> = {}
    for (const outlet of outlets ?? []) {
      const row = (limits ?? []).find((one) => one.outletId === outlet.id)
      next[outlet.id] = { minPax: asField(row?.minPax), maxPax: asField(row?.maxPax) }
    }
    setDraft(next)
    setEverywhere({ minPax: asField(service.minPax), maxPax: asField(service.maxPax) })
  }, [service, outlets, limits])

  // Only the branches that actually sell it: a limit on a branch that does not
  // offer the service is a number nobody will ever meet.
  const selling = (outlets ?? []).filter((outlet) => service?.outletIds.includes(outlet.id))

  const set = (outletId: string, patch: Partial<{ minPax: string; maxPax: string }>) =>
    setDraft((current) => ({
      ...current,
      [outletId]: { ...current[outletId], ...patch },
    }))

  const saveAll = async () => {
    // The default first, so a branch row saved against it is measured against
    // the number that is about to stand rather than the one being replaced.
    await saveService.mutateAsync({
      id: service!.id,
      code: service!.code,
      name: service!.name,
      spaceType: service!.spaceType || undefined,
      sacCode: service!.sacCode ?? '',
      needsDeliveryAddress: service!.needsDeliveryAddress,
      bookable: service!.bookable,
      minPax: asStored(everywhere.minPax),
      maxPax: asStored(everywhere.maxPax),
      sortOrder: service!.sortOrder,
      active: service!.active,
      outletIds: service!.outletIds,
    })
    for (const outlet of selling) {
      const row = draft[outlet.id]
      if (!row) continue
      await save.mutateAsync({
        serviceTypeId: service!.id,
        outletId: outlet.id,
        minPax: asStored(row.minPax),
        maxPax: asStored(row.maxPax),
      })
    }
    onClose()
  }

  return (
    <Dialog open={Boolean(service)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{service?.name} — how many, by branch</DialogTitle>
      <DialogContent dividers>
        <ErrorBanner error={save.error ?? saveService.error} />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          How many one order of this may be taken for. Not the day&apos;s capacity, which is the
          order limits tab: this is one job. Blank is no limit, and a branch left blank follows
          the business.
        </Typography>

        <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Branch</TableCell>
                <TableCell align="right" width={120}>Fewest</TableCell>
                <TableCell align="right" width={120}>Most</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* What every branch follows unless it says otherwise. */}
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>All branches</TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={everywhere.minPax}
                    onChange={(event) =>
                      setEverywhere((current) => ({ ...current, minPax: event.target.value }))
                    }
                    inputProps={{ 'aria-label': 'Fewest everywhere', min: 1 }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={everywhere.maxPax}
                    onChange={(event) =>
                      setEverywhere((current) => ({ ...current, maxPax: event.target.value }))
                    }
                    inputProps={{ 'aria-label': 'Most everywhere', min: 1 }}
                  />
                </TableCell>
              </TableRow>
              {selling.map((outlet) => (
                <TableRow key={outlet.id}>
                  <TableCell>{outlet.name}</TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={draft[outlet.id]?.minPax ?? ''}
                      onChange={(event) => set(outlet.id, { minPax: event.target.value })}
                      inputProps={{ 'aria-label': `Fewest at ${outlet.name}`, min: 1 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={draft[outlet.id]?.maxPax ?? ''}
                      onChange={(event) => set(outlet.id, { maxPax: event.target.value })}
                      inputProps={{ 'aria-label': `Most at ${outlet.name}`, min: 1 }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
        </Table>

        {/* The default is still settable on a service nobody sells yet; only
            the branch rows need a branch. */}
        {selling.length === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            No branch sells this service yet, so there is nothing to vary. Tick the outlets on the
            service to set a branch apart from the rest.
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            disabled={save.isPending || saveService.isPending}
            onClick={() => void saveAll()}
          >
            Save
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  )
}
